'use client';

import type { CSSProperties } from 'react';
import { useGeo } from './GeoProvider';
import { pickGeoValue } from '@/lib/geo';

/** Displays the region-personalised "trusted by" text with social proof */
export function GeoTrustedText({ className }: { className?: string }) {
  const { region } = useGeo();
  return <span className={className}>{region.content.trusted}</span>;
}

/** Displays the payroll feature description based on visitor region */
export function GeoPayrollFeature({ className }: { className?: string }) {
  const { region } = useGeo();
  return <span className={className}>{region.content.payrollFeature}</span>;
}

/** Displays the compliance label for the visitor's region */
export function GeoComplianceLabel({ className }: { className?: string }) {
  const { region } = useGeo();
  return <span className={className}>{region.complianceLabel}</span>;
}

/**
 * A vendor-comparison cell value that switches by region: the INR value for
 * visitors in India, the default (USD/global) value everywhere else. SSR and the
 * first client render both use the GLOBAL default, so there's no hydration
 * mismatch — the swap to INR happens after geo detection resolves.
 */
export function GeoCellText({
  text,
  textInr,
  className,
  style,
  fallback = '—',
}: {
  text?: string;
  textInr?: string;
  className?: string;
  style?: CSSProperties;
  /** Shown when the region has no value (default "—"; pass '' to render nothing). */
  fallback?: string;
}) {
  const { region } = useGeo();
  const value = pickGeoValue(text, textInr, region) || fallback;
  if (!value) return null;
  return (
    <span className={className} style={style}>
      {value}
    </span>
  );
}
